import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { getInitials } from '../lib/utils';
import SubscriptionBanner from './SubscriptionBanner';
import NotificationsBell from './NotificationsBell';
import LiveTimerBadge from './LiveTimerBadge';
import ModuleLockModal from './ModuleLockModal';
import {
  LayoutDashboard,
  Users,
  FileText,
  Receipt,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronRight,
  Building2,
  Package,
  Mic,
  Activity,
  Navigation,
  Calculator,
  Sparkles,
  Shield,
  Briefcase,
  CalendarDays,
  Inbox,
  Mail,
  Boxes,
  ListTodo,
  Lock,
  Send,
} from 'lucide-react';

const navItems = [
  { path: '/dashboard', label: 'Tableau de bord', icon: LayoutDashboard, perm: null },
  { path: '/contacts', label: 'CRM', icon: Users, perm: 'crm' },
  { path: '/calendar', label: 'Agenda', icon: CalendarDays, perm: 'appointments' },
  { path: '/invitations', label: 'Invitations', icon: Inbox, perm: 'appointments' },
  { path: '/tasks', label: 'Tâches', icon: ListTodo, perm: 'tasks', optionalModule: 'tasks' },
  { path: '/meetings', label: 'Réunions', icon: Users, perm: 'crm', optionalModule: 'meetings' },
  { path: '/campaigns', label: 'Campagnes', icon: Send, perm: 'crm' },
  { path: '/quotes', label: 'Devis', icon: FileText, perm: 'quotes' },
  { path: '/invoices', label: 'Factures', icon: Receipt, perm: 'invoices' },
  { path: '/invoices/monthly', label: 'Facturation mensuelle', icon: Receipt, perm: 'invoices' },
  { path: '/price-library', label: 'Bibliothèque', icon: Package, perm: 'price_library' },
  { path: '/stock', label: 'Stocks', icon: Boxes, perm: 'stock', optionalModule: 'stock' },
  { path: '/accounting', label: 'Comptabilité', icon: Calculator, perm: 'accounting' },
  { path: '/activity', label: 'Activité', icon: Activity, perm: null, optionalModule: 'analytics' },
  { path: '/trips/audit', label: 'Audit trajets', icon: Navigation, perm: null },
  { path: '/settings', label: 'Paramètres', icon: Settings, perm: null },
];

const hrNavItems = [
  { path: '/hr/employees', label: 'Employés', icon: Briefcase, perm: 'hr', optionalModule: 'hr' },
  { path: '/hr/leaves', label: 'Congés', icon: CalendarDays, perm: 'hr', optionalModule: 'hr' },
];

const adminNavItems = [
  { path: '/admin/organizations', label: 'Mes clients', icon: Building2 },
  { path: '/admin/settings', label: 'Paramètres', icon: Shield },
  { path: '/admin/users', label: 'Utilisateurs', icon: Users },
  { path: '/admin/kbo', label: 'BCE / KBO', icon: Building2 },
  { path: '/admin/analytics', label: 'Analytics', icon: Sparkles },
  { path: '/email-logs', label: 'Logs email', icon: Mail },
];

const Layout = ({ children }) => {
  const { user, logout, hasPermission, isOwnerOrAdmin, isModuleEnabled } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [lockedModule, setLockedModule] = useState(null);

  const visibleNavItems = navItems.filter((it) => !it.perm || hasPermission(it.perm));
  const visibleHrNavItems = hrNavItems.filter((it) => !it.perm || hasPermission(it.perm));

  const handleNavClick = (item) => (e) => {
    if (item.optionalModule && !isModuleEnabled(item.optionalModule)) {
      e.preventDefault();
      setLockedModule(item.optionalModule);
      return;
    }
    setSidebarOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    // Prefer the longest matching nav entry so that /invoices/monthly doesn't also activate /invoices.
    const candidates = navItems
      .map((n) => n.path)
      .filter((p) => location.pathname === p || location.pathname.startsWith(p + '/'));
    if (!candidates.length) return false;
    const best = candidates.reduce((a, b) => (b.length > a.length ? b : a));
    return best === path;
  };

  // Hide FAB when already on a quote/invoice form (dictation already accessible there)
  // Also hide it if the org hasn't activated the ai_voice module.
  const isOnDocumentForm = /^\/(quotes|invoices)\/[^/]+/.test(location.pathname);
  const showVoiceFab = !isOnDocumentForm && isModuleEnabled('ai_voice');

  const handleVoiceFabClick = () => {
    navigate('/quotes/new?dictation=auto');
  };

  return (
    <div className="app-layout" data-testid="app-layout">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`sidebar fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-200 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0 sidebar-open' : '-translate-x-full lg:translate-x-0'
        }`}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-slate-800/60">
            <Link to="/dashboard" className="flex items-center">
              <img src="/logo-devisgo.png" alt="Devis.Go" className="h-10 w-auto" />
            </Link>
            <button
              className="lg:hidden p-2 hover:bg-slate-800/60 rounded-md text-slate-400"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-5 space-y-0.5 overflow-y-auto">
            {visibleNavItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              const locked = item.optionalModule && !isModuleEnabled(item.optionalModule);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`sidebar-item flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium ${
                    active
                      ? 'bg-slate-800 text-white shadow-sm border-l-2 border-orange-500 pl-[10px]'
                      : locked
                      ? 'text-slate-500 hover:bg-slate-800/40 hover:text-slate-300'
                      : 'text-slate-400 hover:bg-slate-800/50 hover:text-white'
                  }`}
                  onClick={handleNavClick(item)}
                  data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
                >
                  <Icon className="w-[18px] h-[18px]" strokeWidth={1.75} />
                  <span className="flex-1 truncate">{item.label}</span>
                  {locked && <Lock className="w-3 h-3 ml-auto text-amber-500/70" data-testid={`nav-lock-${item.optionalModule}`} strokeWidth={2} />}
                  {!locked && active && <span className="w-1 h-1 rounded-full bg-orange-500 ml-auto" />}
                </Link>
              );
            })}

            {/* HR section */}
            {visibleHrNavItems.length > 0 && (
              <div className="pt-5 mt-3 border-t border-slate-800/60">
                <p className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-violet-400/80 flex items-center gap-1.5">
                  <Briefcase className="w-3 h-3" strokeWidth={2} /> RH
                </p>
                {visibleHrNavItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                const locked = item.optionalModule && !isModuleEnabled(item.optionalModule);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`sidebar-item flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium ${
                      active
                        ? 'bg-violet-600/90 text-white shadow-sm border-l-2 border-violet-300 pl-[10px]'
                        : locked
                        ? 'text-slate-500 hover:bg-slate-800/40'
                        : 'text-slate-400 hover:bg-slate-800/50 hover:text-white'
                    }`}
                    onClick={handleNavClick(item)}
                    data-testid={`nav-hr-${item.path.split('/').pop()}`}
                  >
                    <Icon className="w-[18px] h-[18px]" strokeWidth={1.75} />
                    <span className="flex-1 truncate">{item.label}</span>
                    {locked && <Lock className="w-3 h-3 ml-auto text-amber-500/70" strokeWidth={2} />}
                  </Link>
                );
              })}
            </div>
            )}


            {/* Team section — owner/admin only */}
            {isOwnerOrAdmin() && (
              <div className="pt-5 mt-3 border-t border-slate-800/60">
                <Link
                  to="/team"
                  className={`sidebar-item flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium ${
                    isActive('/team')
                      ? 'bg-orange-500/95 text-white shadow-sm border-l-2 border-orange-200 pl-[10px]'
                      : !isModuleEnabled('team')
                      ? 'text-slate-500 hover:bg-slate-800/40'
                      : 'text-slate-400 hover:bg-slate-800/50 hover:text-white'
                  }`}
                  onClick={handleNavClick({ optionalModule: 'team' })}
                  data-testid="nav-team"
                >
                  <Users className="w-[18px] h-[18px]" strokeWidth={1.75} />
                  <span className="flex-1 truncate">Équipe</span>
                  {!isModuleEnabled('team') && <Lock className="w-3 h-3 ml-auto text-amber-500/70" strokeWidth={2} />}
                  {isActive('/team') && <ChevronRight className="w-4 h-4 ml-auto" />}
                </Link>
              </div>
            )}

            {user?.is_admin && (
              <div className="pt-5 mt-3 border-t border-slate-800/60">
                <p className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-orange-400/80 flex items-center gap-1.5">
                  <Shield className="w-3 h-3" strokeWidth={2} /> Admin
                </p>
                {adminNavItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`sidebar-item flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium ${
                        active
                          ? 'bg-orange-500/95 text-white shadow-sm border-l-2 border-orange-200 pl-[10px]'
                          : 'text-slate-400 hover:bg-slate-800/50 hover:text-white'
                      }`}
                      onClick={() => setSidebarOpen(false)}
                      data-testid={`nav-admin-${item.path.split('/').pop()}`}
                    >
                      <Icon className="w-[18px] h-[18px]" strokeWidth={1.75} />
                      <span className="truncate">{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            )}
          </nav>

          {/* User section */}
          <div className="px-3 py-3 border-t border-slate-800/60">
            <div className="flex items-center gap-3 px-2 py-1.5 rounded-md hover:bg-slate-800/40 transition-colors cursor-pointer">
              <Avatar className="h-9 w-9 ring-2 ring-slate-700">
                <AvatarFallback className="bg-gradient-to-br from-orange-500 to-orange-600 text-white text-sm font-semibold">
                  {getInitials(user?.first_name, user?.last_name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-100 truncate">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="main-content">
        <SubscriptionBanner />
        {/* Header */}
        <header className="main-header h-16 flex items-center justify-between px-6">
          <div className="flex items-center gap-4">
            <button
              className="lg:hidden p-2 hover:bg-slate-100 rounded-md"
              onClick={() => setSidebarOpen(true)}
              data-testid="sidebar-toggle"
            >
              <Menu className="w-5 h-5" strokeWidth={1.75} />
            </button>
            {user?.company_name && (
              <div className="hidden sm:flex items-center gap-2 text-sm font-medium text-slate-500">
                <Building2 className="w-4 h-4" strokeWidth={1.75} />
                <span>{user.company_name}</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-1.5">
            <LiveTimerBadge />
            <NotificationsBell />
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2 h-9 px-2 hover:bg-slate-100/80 rounded-lg" data-testid="user-menu-trigger">
                <Avatar className="h-7 w-7 ring-1 ring-slate-200">
                  <AvatarFallback className="bg-gradient-to-br from-orange-500 to-orange-600 text-white text-xs font-semibold">
                    {getInitials(user?.first_name, user?.last_name)}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden sm:inline text-sm font-medium text-slate-700">
                  {user?.first_name}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem asChild>
                <Link to="/settings" className="flex items-center gap-2 cursor-pointer">
                  <Settings className="w-4 h-4" />
                  Paramètres
                </Link>
              </DropdownMenuItem>
              {/* Abonnement entry hidden during FREE_MODE launch — re-enable once paid plans go live */}
              {user?.is_admin && (
                <DropdownMenuItem asChild>
                  <Link to="/admin/users" className="flex items-center gap-2 cursor-pointer">
                    <Shield className="w-4 h-4" />
                    Administration
                  </Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleLogout}
                className="text-red-600 cursor-pointer"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Déconnexion
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 sm:p-6">{children}</main>
      </div>

      {/* Floating Voice Quote Button */}
      {showVoiceFab && (
        <button
          onClick={handleVoiceFabClick}
          aria-label="Nouveau devis vocal"
          data-testid="voice-fab"
          className="group fixed bottom-20 right-6 z-50 flex items-center gap-3 h-14 pl-4 pr-5 rounded-full bg-orange-500 text-white shadow-xl hover:shadow-2xl hover:bg-orange-600 active:scale-95 transition-all duration-200"
        >
          <span className="relative flex items-center justify-center w-9 h-9">
            <span className="absolute inset-0 rounded-full bg-white/20 animate-ping" />
            <Mic className="w-5 h-5 relative" />
          </span>
          <span className="hidden sm:inline text-sm font-semibold whitespace-nowrap">
            Devis vocal
          </span>
        </button>
      )}

      {/* Optional-module gate modal */}
      <ModuleLockModal
        moduleKey={lockedModule}
        open={!!lockedModule}
        onOpenChange={(open) => !open && setLockedModule(null)}
        onActivated={() => {
          // After activation, navigate to the originally-clicked path if known
          // (the modal currently doesn't know the path; user can re-click).
        }}
      />
    </div>
  );
};

export default Layout;
